<template>
  <video
    v-if="autoplay"
    ref="videoRef"
    playsinline
    :autoplay="autoplay"
    :muted="autoplay"
    :loop="autoplay"
    :controls="!autoplay"
    :poster="posterSrc"
    :style="placeholderStyle"
    @loadeddata="onLoaded"
    @error="onError"
    class="custom-video"
  >
    <source :src="videoSrc" :type="type" />
  </video>
  <video
    v-else
    ref="videoPlayer"
    class="video-js h-auto"
    preload="auto"
    playsinline
    controls
    :poster="posterSrc"
    :style="placeholderStyle"
    @loadeddata="onLoaded"
    @error="onError"
    disablePictureInPicture
  >
    <source :src="videoSrc" :type="type" />
  </video>
</template>

<script setup>
const props = defineProps({
  src: { type: String },
  type: { type: String },
  placeholder: { type: String },
  autoplay: { type: Boolean },
});

const runtimeConfig = useRuntimeConfig();
const baseUrl = runtimeConfig.public.baseUrl;
const placeholderSrc = "/img/placeholder-img.webp";

const videoRef = ref(null);
const videoPlayer = ref(null);
const isLoaded = ref(false);
let player;
let canPlayHandler;
let isDisposed = false;

const buildAbsoluteSrc = (value) => {
  if (!value) return "";
  return value.startsWith("http") ? value : `${baseUrl}${value}`;
};

const videoSrc = computed(() => buildAbsoluteSrc(props.src));
const posterSrc = computed(
  () => buildAbsoluteSrc(props.placeholder) || placeholderSrc
);

const placeholderStyle = computed(() => ({
  backgroundImage: isLoaded.value ? "none" : `url(${posterSrc.value})`,
  backgroundSize: "cover",
  backgroundPosition: "center",
  backgroundRepeat: "no-repeat",
}));

watch(videoSrc, () => {
  isLoaded.value = false;
});

onMounted(async () => {
  if (!props.autoplay && videoPlayer.value) {
    const [{ default: videojs }] = await Promise.all([
      import("video.js"),
      import("video.js/dist/video-js.css"),
    ]);

    if (!videoPlayer.value || isDisposed) return;

    player = videojs(videoPlayer.value, {
      controls: true,
      controlBar: {
        playToggle: true,
        currentTimeDisplay: true,
        timeDivider: true,
        durationDisplay: true,
        remainingTimeDisplay: true,
        progressControl: true,
        fullscreenToggle: true,
        volumePanel: true,
        pipButton: true,
      },
      responsive: true,
      fluid: true,
    });
  }

  if (props.autoplay && videoRef.value) {
    // Ensure muted for autoplay compliance
    videoRef.value.setAttribute("muted", "");
    videoRef.value.muted = true;
    const tryPlay = () => {
      if (!videoRef.value || !props.autoplay) return;
      if (videoRef.value.paused) {
        videoRef.value.play().catch((error) => {
          console.error("Autoplay error:", error);
        });
      }
    };

    if (videoRef.value.readyState >= 2) {
      tryPlay();
    } else {
      canPlayHandler = () => {
        tryPlay();
      };
      videoRef.value.addEventListener("canplay", canPlayHandler, { once: true });
    }
  }
});

onBeforeUnmount(() => {
  isDisposed = true;
  if (canPlayHandler && videoRef.value) {
    videoRef.value.removeEventListener("canplay", canPlayHandler);
  }
  if (player) {
    player.dispose();
  }
});

const onLoaded = () => {
  isLoaded.value = true;
};

const onError = () => {
  isLoaded.value = false;
};
</script>
<style>
.video-js .vjs-big-play-button {
  @apply bg-[url('/icon/play-circle-2.svg')] bg-contain bg-transparent h-12 w-12 -mt-6 -ml-6 lg:h-20 lg:w-20 border-0 lg:-mt-10 lg:-ml-10 !important;
}
.vjs-control-text,
.vjs-icon-placeholder {
  @apply hidden;
}
</style>
